import type { Clip, Mischmodus, Timeline, Track, TransitionKind } from '@schnitt/shared';
import { sortedClips, duckGain, gainAt, speechIntervals, dipFarbe, uebergangHatForm, uebergangsDip, uebergangsRaster, uebergangsSchub, hatRampe, quellZeit, rampeVon } from '@schnitt/shared';

/**
 * Working out what is on screen at a given moment.
 *
 * The browser preview is an approximation and says so: it plays the proxies
 * through real media elements and composites overlays with CSS. Colour
 * adjustments map onto CSS filters, a LUT does not map onto anything, and
 * transitions other than a crossfade are shown as a crossfade. The render on
 * the server is the authoritative picture; this is the thing you cut with.
 */

/**
 * **Welcher Übergang gerade läuft und wie weit** (7.147).
 *
 * Nur für die Arten, die eine *Form* haben: die Leinwand schneidet sie aus,
 * statt überzublenden. `deckung` ist die Deckung **ohne** den Übergang --
 * der alte Weg rechnet ihn weiter in `opacity` hinein, und beide Wege
 * sollen aus derselben Liste lesen können.
 */
export interface LaufenderUebergang {
  art: TransitionKind;
  p: number;
  deckung: number;
}

export interface VisibleBase {
  clip: Clip;
  /** Which of the two pooled video elements plays it. */
  slot: 0 | 1;
  opacity: number;
  /** Seconds into the source file. */
  mediaTime: number;
  uebergang?: LaufenderUebergang;
  /**
   * **Welche Seite einer Überblendung dieser Clip ist** (7.157) -- nur auf
   * der Basisspur und nur, wenn die Blende über die Deckung läuft (nicht beim
   * Weg durch eine Farbe und nicht beim Schieben).
   *
   * Gebraucht wird das im Weg ohne Leinwand: dort liegen die beiden
   * Videoelemente in fester Reihenfolge übereinander, und der kommende Clip
   * liegt jedes zweite Mal *unten*. Siehe `deckungImStapel` in Preview.tsx.
   */
  blende?: { rolle: 'kommt' | 'geht'; p: number; ohne: number };
  /**
   * **Wie grob das Bild gerade abgelesen wird** (`pixelize`, 7.148): die
   * Seite eines Feldes als Anteil der kürzeren Bildseite. Das ist keine
   * Form -- überblendet wird ganz gewöhnlich --, sondern eine andere
   * Abtaststelle, und sie gilt für beide Clips gleichzeitig.
   */
  raster?: number;
  /**
   * **Der Weg durch eine Farbe** (7.149): liegt hier eine Farbe an, gehört
   * sie *unter* diesen Clip und deckt alles. Was danach kommt, liegt mit
   * seinem Gewicht darüber.
   */
  dipFuellung?: string;
  /**
   * **Wie weit die Ebene seitlich steht** (7.148): der Versatz eines
   * Schiebe-Übergangs, in Anteilen der Leinwandbreite. Beide Bilder wandern,
   * und zusammen decken sie das Bild genau ab.
   */
  schub?: number;
}

export interface VisibleOverlay {
  clip: Clip;
  trackId: string;
  opacity: number;
  mediaTime: number;
  /** Wie die Spur auf der darunter liegt (S10). */
  mischmodus: Mischmodus;
  uebergang?: LaufenderUebergang;
  /**
   * **Wie grob das Bild gerade abgelesen wird** (`pixelize`, 7.148): die
   * Seite eines Feldes als Anteil der kürzeren Bildseite. Das ist keine
   * Form -- überblendet wird ganz gewöhnlich --, sondern eine andere
   * Abtaststelle, und sie gilt für beide Clips gleichzeitig.
   */
  raster?: number;
  schub?: number;
}

export interface AudibleClip {
  clip: Clip;
  trackId: string;
  mediaTime: number;
  volume: number;
}

function fadeFactor(clip: Clip, local: number): number {
  let factor = 1;
  if (clip.fadeIn > 0 && local < clip.fadeIn) factor *= Math.max(0, local / clip.fadeIn);
  const outStart = clip.duration - clip.fadeOut;
  if (clip.fadeOut > 0 && local > outStart) {
    factor *= Math.max(0, 1 - (local - outStart) / clip.fadeOut);
  }
  return factor;
}

/**
 * Wo im Material der Clip `local` Sekunden nach seinem Anfang steht.
 *
 * Mit einer Rampe (S6) ist das die Fläche unter der Tempokurve, wie beim
 * Abspielen (`medienzeitJetzt`) und im Film (7.192): bis zum 3. Oktober
 * rechnete hier „Zeit mal Anfangstempo“, und im Stehen zeigte ein Clip mit
 * Rampe ein anderes Bild als der Film -- bei 1 → 3 in 4 s nach 2 s die
 * Quellsekunde 2 statt 3. Ohne Rampe bleibt es bei der alten Rechnung, auch
 * über das Ende hinaus (ein Clip, der für einen Übergang stehen bleibt).
 */
function medienzeit(clip: Clip, local: number): number {
  return hatRampe(clip) ? clip.sourceIn + quellZeit(local, clip.duration, rampeVon(clip)) : clip.sourceIn + local * clip.speed;
}

export function baseTrack(timeline: Timeline): Track | undefined {
  return timeline.tracks.find((t) => t.kind === 'video');
}

/**
 * The base-track clips visible at `t`. During a transition two are returned:
 * the outgoing one underneath and the incoming one fading in over it.
 */
export function visibleBase(timeline: Timeline, t: number): VisibleBase[] {
  const track = baseTrack(timeline);
  if (!track || track.hidden) return [];
  const clips = sortedClips(track);
  const out: VisibleBase[] = [];

  clips.forEach((clip, index) => {
    const transition = clip.transitionIn?.duration ?? 0;
    const next = clips[index + 1];
    const nextTransition = next?.transitionIn?.duration ?? 0;
    // A clip stays on screen through the transition that replaces it.
    const from = clip.start;
    const to = next && nextTransition > 0 ? Math.max(clip.start + clip.duration, next.start + nextTransition) : clip.start + clip.duration;
    if (t < from || t >= to) return;

    const local = t - clip.start;
    const ohneUebergang = fadeFactor(clip, Math.min(local, clip.duration));
    let opacity = ohneUebergang;
    let uebergang: LaufenderUebergang | undefined;
    let fuellung: string | undefined;
    let schub: number | undefined;
    let raster: number | undefined;
    let blende: { rolle: 'kommt' | 'geht'; p: number; ohne: number } | undefined;
    if (transition > 0 && local < transition) {
      const p = Math.max(0, Math.min(1, local / transition));
      opacity *= p;
      const art = clip.transitionIn?.kind;
      // Hat die Art eine Form, malt die Leinwand sie (7.147); die Deckung
      // bleibt daneben stehen, weil der alte Weg weiter überblendet.
      if (art && uebergangHatForm(art)) uebergang = { art, p, deckung: ohneUebergang };
      // Geht sie durch eine Farbe, zählt nur das gemessene Gewicht (7.149).
      const dip = art ? uebergangsDip(art, p) : null;
      if (dip) opacity = ohneUebergang * dip.neu;
      // Und schiebt sie, deckt die Ebene voll und steht nur woanders.
      const geschoben = art ? uebergangsSchub(art, p) : null;
      if (geschoben) {
        opacity = ohneUebergang;
        schub = geschoben.neu;
      }
      // Und das Raster lässt die Deckung, wie sie ist -- es liest nur gröber.
      const feld = art ? uebergangsRaster(art, p) : 0;
      if (feld > 0) raster = feld;
      if (!dip && !geschoben) blende = { rolle: 'kommt', p, ohne: ohneUebergang };
    }
    // **Und derselbe Übergang von der anderen Seite.** Dieser Clip *geht*,
    // während der nächste kommt: sein Gewicht ist `alt`, geteilt durch das,
    // was der neue davon noch stehenlässt -- so ergibt das Übereinanderlegen
    // genau die Summe der beiden Gewichte.
    const naechsteArt = next && nextTransition > 0 ? next.transitionIn?.kind : undefined;
    const dipNaechster = naechsteArt && next && t >= next.start ? uebergangsDip(naechsteArt, (t - next.start) / nextTransition) : null;
    if (dipNaechster && naechsteArt) {
      opacity = dipNaechster.neu >= 1 ? 0 : ohneUebergang * (dipNaechster.alt / (1 - dipNaechster.neu));
      fuellung = dipFarbe(naechsteArt) ?? undefined;
    }
    const schubNaechster = naechsteArt && next && t >= next.start ? uebergangsSchub(naechsteArt, (t - next.start) / nextTransition) : null;
    if (schubNaechster) schub = schubNaechster.alt;
    // Der gehende Clip wird genauso grob abgelesen wie der kommende -- sonst
    // stünde ein klobiges Bild über einem feinen.
    const rasterNaechster = naechsteArt && next && t >= next.start ? uebergangsRaster(naechsteArt, (t - next.start) / nextTransition) : 0;
    if (rasterNaechster > 0) raster = rasterNaechster;
    if (naechsteArt && next && t >= next.start && !dipNaechster && !schubNaechster) {
      blende = { rolle: 'geht', p: Math.max(0, Math.min(1, (t - next.start) / nextTransition)), ohne: ohneUebergang };
    }

    out.push({
      clip,
      slot: (index % 2) as 0 | 1,
      opacity,
      mediaTime: medienzeit(clip, Math.max(0, local)),
      ...(uebergang ? { uebergang } : {}),
      ...(fuellung ? { dipFuellung: fuellung } : {}),
      ...(schub !== undefined ? { schub } : {}),
      ...(raster !== undefined ? { raster } : {}),
      ...(blende ? { blende } : {}),
    });
  });
  return out;
}

export function visibleOverlays(timeline: Timeline, t: number): VisibleOverlay[] {
  const out: VisibleOverlay[] = [];
  const videoTracks = timeline.tracks.filter((tr) => tr.kind === 'video');
  for (const track of videoTracks.slice(1)) {
    if (track.hidden) continue;
    const clips = sortedClips(track);
    clips.forEach((clip, index) => {
      // **Ein Übergang hält den vorigen Clip auf dem Schirm** (18. September,
      // „jedes andere schnittprogramm kann das auch überall“). Dieselbe
      // Rechnung wie unten auf der Basisspur: der abgelöste Clip bleibt, bis
      // der Übergang vorbei ist, und der neue blendet darüber auf. Die
      // Vorschau nähert jede Art mit einer Überblendung an -- so hält sie es
      // unten seit je.
      const naechster = clips[index + 1];
      const naechsterUebergang = naechster?.transitionIn?.duration ?? 0;
      const bis =
        naechster && naechsterUebergang > 0
          ? Math.max(clip.start + clip.duration, naechster.start + naechsterUebergang)
          : clip.start + clip.duration;
      const local = t - clip.start;
      if (t < clip.start || t >= bis) return;
      const ohneUebergang = clip.transform.opacity * fadeFactor(clip, Math.min(local, clip.duration));
      let opacity = ohneUebergang;
      const uebergang = clip.transitionIn?.duration ?? 0;
      let form: LaufenderUebergang | undefined;
      let raster: number | undefined;
      if (uebergang > 0 && local < uebergang) {
        const p = Math.max(0, Math.min(1, local / uebergang));
        opacity *= p;
        const art = clip.transitionIn?.kind;
        if (art && uebergangHatForm(art)) form = { art, p, deckung: ohneUebergang };
        const feld = art ? uebergangsRaster(art, p) : 0;
        if (feld > 0) raster = feld;
      }
      // Brand templates animate themselves in and out in the render, so the
      // preview does the same or the timing would look wrong.
      if (clip.source.kind === 'brand') {
        if (clip.fadeIn === 0) opacity *= Math.min(1, local / 0.35);
        if (clip.fadeOut === 0 && clip.duration > 0.8) {
          opacity *= Math.min(1, Math.max(0, (clip.duration - local) / 0.35));
        }
      }
      out.push({
        clip,
        trackId: track.id,
        opacity,
        mediaTime: medienzeit(clip, Math.max(0, local)),
        mischmodus: track.mischmodus ?? 'normal',
        ...(form ? { uebergang: form } : {}),
        ...(raster !== undefined ? { raster } : {}),
      });
    });
  }
  return out;
}

export function audibleClips(timeline: Timeline, t: number, hasAudio: (assetId: string) => boolean = () => true): AudibleClip[] {
  const out: AudibleClip[] = [];
  // The same envelope the export uses, so a music bed sits at the same level
  // under a voice here as it will in the file.
  const speech = speechIntervals(timeline, hasAudio);
  for (const track of timeline.tracks) {
    if (track.muted) continue;
    const duck = track.duck ? duckGain(speech, t, track.duckLevel) : 1;
    for (const clip of sortedClips(track)) {
      if (clip.muted || clip.source.kind !== 'asset') continue;
      const local = t - clip.start;
      if (local < 0 || local >= clip.duration) continue;
      out.push({
        clip,
        trackId: track.id,
        mediaTime: medienzeit(clip, local),
        // gainAt statt clip.gain: hat der Clip einen Pegelverlauf, gilt der
        // Wert an dieser Stelle -- dieselbe Gerade, die der Export rechnet.
        //
        // **Und nicht bei 1 gedeckelt** (Sascha, 15. Oktober: „eine m4a als
        // Voiceover … ultra leise, obwohl es 400 % bekommt -- HOTFIX“). Der
        // Export verstärkt bis 4 × Clip mal 2 × Spur; die Vorschau spielte
        // höchstens 100 %. Ein leises Voiceover, das sich beim Einfügen 400 %
        // gab (7.210), klang hier wie vorher. Die Tonkette regelt mit einem
        // `GainNode`, der mehr als 1 kann; ein Medienelement kann es nicht und
        // deckelt selbst, wo der Wert ankommt.
        volume: Math.max(0, Math.min(8, gainAt(clip, local) * track.gain * duck * fadeFactor(clip, local))),
      });
    }
  }
  return out;
}

/**
 * Colour effects as a CSS filter. `blur` and `colorAdjust` map closely enough
 * to trust; a LUT has no CSS equivalent, so the UI says it applies on export
 * rather than showing a wrong colour and calling it a preview.
 */
/**
 * The clip's effects as a CSS filter list, in the order the renderer applies
 * them. A look points at an SVG filter the preview renders next to the
 * element (see looks.tsx); the vignette is drawn as its own layer, because
 * CSS has no filter for it.
 */
export function cssFilter(clip: Clip, deckend = false): string {
  return cssFilterVon(clip.effects, clip.id, deckend);
}

/**
 * **Weichzeichnen ohne weiche Kante** (7.173).
 *
 * Der Film zeichnet das Bild der Quelle weich (`gblur`, am Rand fortgesetzt)
 * und legt es dann mit harter Kante auf. CSS `blur()` rechnet ausserhalb des
 * Elements mit Durchsichtigem und franst die Kante aus -- gemessen im
 * Wege-Vergleich: 85 von 255 an der Kante. Dahinter steht darum dieser
 * Filter: Deckkraft wieder 1, geschnitten am Kasten des Elements. Weil der
 * Browser dabei die Farbe durch die Deckkraft teilt, ist das Ergebnis ein
 * Mittel nur über das, was im Kasten liegt -- wie im Film.
 *
 * Nur für Quellen, die ganz decken (Farbe, Video): ein Bild mit
 * durchsichtigen Stellen würde damit zugemalt.
 */
export const KANTE_FILTER_ID = 'weich-harte-kante';

/**
 * Dasselbe für eine Liste von Effekten, die (noch) an keinem Clip hängt.
 *
 * Gebraucht seit 7.16: die Vorschaukacheln zeigen, was ein Look oder eine
 * Vorlage *täte*. Sie gehen bewusst durch dieselbe Funktion wie die Leinwand
 * -- eine zweite Umrechnung daneben wäre eine Kachel, die etwas anderes zeigt
 * als das Bild danach, und das ist schlimmer als gar keine Kachel.
 */
export function cssFilterVon(effects: Clip['effects'], id: string, deckend = false): string {
  const parts: string[] = [];
  for (const fx of effects) {
    if (fx.type === 'colorAdjust') {
      // Die Reihenfolge ist die der einen Rechnung (`farbwerteAuf`):
      // Helligkeit, Kontrast, **Gamma**, Sättigung. Das Gamma kann CSS nicht,
      // also steht dort ein SVG-Filter dazwischen (7.133) -- dieselbe
      // Bauweise wie beim Look und bei den Farbrädern.
      parts.push(`brightness(${1 + fx.brightness})`, `contrast(${fx.contrast})`);
      // Die Kennung steht hier als Zeichenkette, wie beim Look eine Zeile
      // weiter unten: `looks.tsx` hängt am Stilblatt und an React, und diese
      // Datei soll das nicht.
      if (fx.gamma !== 1) parts.push(`url(#gamma-${id})`);
      parts.push(`saturate(${fx.saturation})`);
    } else if (fx.type === 'blur') {
      // In Punkten des Films, wie der Export (7.173): der Export passt die
      // Quelle ins Bild ein und zeichnet dann mit `sigma=amount` weich. Bis
      // zum 25. September stand hier `amount / 2` Punkte des Bildschirms --
      // auf der kleinen Leinwand im Telefon fast dreimal zu weich, auf dem
      // grossen Bildschirm zu scharf. Ohne Leinwand drumherum (die Kacheln)
      // gilt der alte halbe Punkt.
      parts.push(`blur(calc(var(--film-punkt, 0.5px) * ${fx.amount}))`);
      if (deckend && fx.amount > 0) parts.push(`url(#${KANTE_FILTER_ID})`);
    } else if (fx.type === 'sharpen') {
      parts.push(`contrast(${1 + fx.amount * 0.08})`);
    } else if (fx.type === 'lut') {
      parts.push(`url(#look-${id})`);
    } else if (fx.type === 'farbraeder') {
      // Nur, wenn wirklich etwas gestellt ist: ein `url(#…)` auf einen Filter,
      // den niemand gezeichnet hat, lässt das Bild im Browser verschwinden.
      const alle = [...fx.tiefen, ...fx.mitten, ...fx.hoehen];
      if (alle.some((v) => Math.abs(v) > 1e-6)) parts.push(`url(#farbraeder-${id})`);
    }
  }
  return parts.join(' ');
}

/**
 * Dieselben drei Werte als Zahlen, für die Leinwand, die selbst malt (7.69).
 *
 * `cssFilterVon` schreibt sie als Zeichenkette für den Browser; die
 * Grafikkarte braucht sie als Zahlen. Beide lesen aus derselben Effektliste,
 * damit nicht die eine Vorschau anders aussieht als die andere.
 *
 * Was hier nicht steht: Weichzeichnen, Schärfen und der Look. Die kommen mit
 * 7.69c als eigene Shader -- solange sie fehlen, malt die Leinwand ein Bild
 * ohne sie, und genau deshalb steht sie noch hinter einem Schalter.
 */
export function farbwerteVon(effects: Clip['effects']): [number, number, number, number] {
  let helligkeit = 1;
  let kontrast = 1;
  let saettigung = 1;
  let gamma = 1;
  for (const fx of effects) {
    if (fx.type === 'colorAdjust') {
      helligkeit *= 1 + fx.brightness;
      kontrast *= fx.contrast;
      saettigung *= fx.saturation;
      // Das Gamma zählt seit 7.133 mit: zwei Vorlagen setzen es („Heller“
      // 1,06, „Dunkler“ 0,94), und die Vorschau liess es bis dahin fallen.
      gamma *= fx.gamma;
    } else if (fx.type === 'sharpen') {
      kontrast *= 1 + fx.amount * 0.08;
    }
  }
  return [helligkeit, kontrast, saettigung, gamma];
}

/** How dark the corners go, 0..1, or 0 when the clip has no vignette. */
export function vignetteAmount(clip: Clip): number {
  return vignetteVon(clip.effects);
}

/** Dasselbe für eine Effektliste ohne Clip -- für die Kacheln (7.16). */
export function vignetteVon(effects: Clip['effects']): number {
  const fx = effects.find((e) => e.type === 'vignette');
  return fx && fx.type === 'vignette' ? fx.amount : 0;
}

/*
 * Hier stand `unlockMediaElements`: bei der ersten Berührung irgendwo im
 * Schnittplatz einmal über alles laufen, `muted = true` setzen, `play()`,
 * `pause()`. Der Gedanke war richtig, die Ausführung hat den Schaden erst
 * angerichtet -- siehe tonkette.ts: ein *stummes* `play()` braucht auf iOS
 * gar keine Erlaubnis und beweist deshalb auch keine. Der Anlauf aus der
 * Berührung war damit verbraucht, und was danach wirklich klingen oder
 * laufen sollte, wurde abgewiesen. Dazu blieben die Tonelemente stumm
 * zurück: gesetzt wurde `muted`, zurückgesetzt nie.
 *
 * Freigeschaltet wird jetzt an einer Stelle, die weiß, was das Element
 * gleich tun soll: der Freischalter der Vorschau, aus dem Tippen auf
 * *Abspielen* heraus (Preview.tsx, ton.ts).
 */
